import React from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import {
  ShoppingBagIcon,
  CubeIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  EyeIcon
} from '@heroicons/react/24/outline';

import api from '../../services/api';
import VendorLayout from '../../components/layout/VendorLayout';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import StatCard from '../../components/ui/StatCard';

const Dashboard = () => {
  // جلب إحصائيات البائع
  const { data: stats, isLoading, error } = useQuery(
    'vendor-stats',
    () => api.get('/vendors/dashboard/stats').then(res => res.data.data),
    {
      refetchInterval: 30000, // تحديث كل 30 ثانية
      retry: 1,
      onError: (error) => {
        console.error('خطأ في جلب الإحصائيات:', error);
      }
    }
  );

  // جلب الطلبات الحديثة
  const { data: recentOrders } = useQuery(
    'recent-orders',
    () => api.get('/orders/vendor/orders', { params: { limit: 5 } }).then(res => res.data.data?.orders || [])
  );

  // جلب المنتجات منخفضة المخزون
  const { data: lowStockProducts } = useQuery(
    'low-stock-products',
    () => api.get('/products/vendor/my-products', { params: { limit: 5 } }).then(res => {
      const products = res.data.data?.products || [];
      return products.filter(product => product.quantity <= 10);
    })
  );

  if (isLoading) {
    return (
      <VendorLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </VendorLayout>
    );
  }

  if (error) {
    return (
      <VendorLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-red-900 mb-2">خطأ في تحميل البيانات</h3>
          <p className="text-red-700">
            {error.response?.data?.message || 'حدث خطأ في جلب بيانات المتجر. يرجى المحاولة لاحقاً.'}
          </p>
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="space-y-6">
        {/* ترحيب */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-6 text-white">
          <h1 className="text-2xl font-bold mb-2">مرحباً بك في لوحة التحكم</h1>
          <p className="text-primary-100">
            إدارة متجرك ومنتجاتك وطلباتك من مكان واحد
          </p>
        </div>

        {/* الإحصائيات الرئيسية */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="إجمالي المنتجات"
            value={stats?.products?.total || 0}
            icon={CubeIcon}
            color="blue"
            subtitle={`${stats?.products?.active || 0} منتج نشط`}
          />
          
          <StatCard
            title="إجمالي الطلبات"
            value={stats?.orders?.total || 0}
            icon={ShoppingBagIcon}
            color="green"
            subtitle={`${stats?.orders?.pending || 0} طلب معلق`}
          />
          
          <StatCard
            title="إجمالي المبيعات"
            value={`${parseFloat(stats?.sales?.total || 0).toLocaleString()} جنيه`}
            icon={ChartBarIcon}
            color="yellow"
            subtitle={`${stats?.sales?.quantity || 0} قطعة مباعة`}
          />
          
          <StatCard
            title="تقييم المتجر"
            value={`${parseFloat(stats?.vendor?.rating?.average || 0).toFixed(1)}/5`}
            icon={EyeIcon}
            color="purple"
            subtitle={`${stats?.vendor?.rating?.count || 0} تقييم`}
          />
        </div>

        {/* إجراءات سريعة */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-lg font-semibold mb-4">إجراءات سريعة</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to="/vendor/products/add"
              className="flex items-center justify-center p-4 border-2 border-dashed border-primary-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
            >
              <PlusIcon className="w-6 h-6 text-primary-600 ml-2" />
              <span className="text-primary-600 font-medium">إضافة منتج جديد</span>
            </Link>
            
            <Link
              to="/vendor/orders"
              className="flex items-center justify-center p-4 border-2 border-dashed border-green-300 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors"
            >
              <ShoppingBagIcon className="w-6 h-6 text-green-600 ml-2" />
              <span className="text-green-600 font-medium">إدارة الطلبات</span>
            </Link>
            
            <Link
              to="/vendor/profile"
              className="flex items-center justify-center p-4 border-2 border-dashed border-blue-300 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <CubeIcon className="w-6 h-6 text-blue-600 ml-2" />
              <span className="text-blue-600 font-medium">إعدادات المتجر</span>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* الطلبات الحديثة */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">الطلبات الحديثة</h2>
              <Link
                to="/vendor/orders"
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                عرض الكل
              </Link>
            </div>
            
            {recentOrders && recentOrders.length > 0 ? (
              <div className="space-y-3">
                {recentOrders.map((order) => (
                  <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm">طلب #{order.order_number}</p>
                      <p className="text-xs text-gray-500">
                        {order.user?.name} • {new Date(order.created_at).toLocaleDateString('ar-SA')}
                      </p>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm">{order.total_amount} جنيه</p>
                      <span className={`inline-flex px-2 py-1 text-xs rounded-full ${
                        order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'shipped' ? 'bg-purple-100 text-purple-800' :
                        order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status === 'pending' ? 'معلق' :
                         order.status === 'processing' ? 'قيد المعالجة' :
                         order.status === 'shipped' ? 'مشحون' :
                         order.status === 'delivered' ? 'مسلم' : order.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <ShoppingBagIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>لا توجد طلبات حديثة</p>
              </div>
            )}
          </div>

          {/* المنتجات منخفضة المخزون */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold flex items-center">
                <ExclamationTriangleIcon className="w-5 h-5 text-orange-500 ml-2" />
                منتجات منخفضة المخزون
              </h2>
              <Link
                to="/vendor/products"
                className="text-primary-600 hover:text-primary-700 text-sm font-medium"
              >
                عرض الكل
              </Link>
            </div>
            
            {lowStockProducts && lowStockProducts.length > 0 ? (
              <div className="space-y-3">
                {lowStockProducts.map((product) => (
                  <div key={product.id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center">
                      {product.images?.[0]?.url && (
                        <img
                          src={product.images[0].url}
                          alt={product.name}
                          className="w-10 h-10 rounded-lg object-cover ml-3"
                        />
                      )}
                      <div>
                        <p className="font-medium text-sm">{product.name}</p>
                        <p className="text-xs text-gray-500">السعر: {product.price} جنيه</p>
                      </div>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-sm text-orange-600">
                        {product.quantity} متبقي
                      </p>
                      <p className="text-xs text-gray-500">
                        الحد الأدنى: 10
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <CubeIcon className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>جميع المنتجات متوفرة بكميات كافية</p>
              </div>
            )}
          </div>
        </div>

        {/* نصائح للبائعين الجدد */}
        {stats?.products?.total === 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-blue-900 mb-3">مرحباً بك كبائع جديد!</h3>
            <div className="space-y-2 text-blue-800">
              <p>• ابدأ بإضافة منتجاتك الأولى لجذب العملاء</p>
              <p>• تأكد من إضافة صور عالية الجودة ووصف مفصل</p>
              <p>• حدد أسعاراً تنافسية لمنتجاتك</p>
              <p>• تفاعل مع طلبات العملاء بسرعة</p>
            </div>
            <Link
              to="/vendor/products/add"
              className="inline-flex items-center mt-4 btn-primary"
            >
              <PlusIcon className="w-4 h-4 ml-2" />
              إضافة أول منتج
            </Link>
          </div>
        )}
      </div>
    </VendorLayout>
  );
};

export default Dashboard;